/**
 * Runtime white-label branding (plan §14.2 item 3, ADR 0007 / 0012). Before the first render the app asks
 * GET /v1/branding which brand this host carries, then overrides the CSS tokens (accent, text on accent, links,
 * page background), the favicon and the page title. One build serves every brand; nothing is rebuilt.
 * The colours were WCAG-checked by the API when they were saved; here every value is re-validated before it
 * reaches CSS, so a bad response can never inject styles.
 */
import { create } from 'zustand';
import { api } from '@/lib/api/endpoints';
import type { PublicBranding, ThemeTokens } from '@/lib/api/types';
import { logInfo, logWarn } from '@/lib/log';
import { usePrefs } from '@/store/prefs';

export interface BrandingState {
  branding: PublicBranding | null;
  status: 'loading' | 'ready' | 'error';
  set: (b: PublicBranding | null, status: BrandingState['status']) => void;
}

export const useBranding = create<BrandingState>()((set) => ({
  branding: null,
  status: 'loading',
  set: (branding, status) => set({ branding, status }),
}));

/** True on a tenant's own domain (the tenant's brand is showing). */
export const isTenantBrand = (b: PublicBranding | null): b is PublicBranding => b?.source === 'tenant';

const HEX = /^#[0-9a-fA-F]{6}$/;
/** Set when a person picks a language themselves; a tenant's default language never overrides that choice. */
export const LOCALE_CHOSEN_KEY = 'cf.locale-chosen';
const STYLE_ID = 'cf-brand';

/** Only our own asset paths or https URLs may become a favicon or logo source. */
export function safeAssetUrl(u: string | null | undefined): string | null {
  if (!u) return null;
  if (/^\/v1\/branding\/assets\/[0-9a-f-]{36}\/[a-z_]+-[0-9a-f]{16}\.(png|jpg|webp|svg|ico)$/.test(u)) return u;
  return /^https:\/\/[^\s"'<>]+$/i.test(u) ? u : null;
}

function block(selector: string, t: ThemeTokens): string {
  const lines: string[] = [];
  if (HEX.test(t.accent)) lines.push(`--accent: ${t.accent};`);
  if (HEX.test(t.on_accent)) lines.push(`--on-accent: ${t.on_accent};`);
  if (HEX.test(t.accent_text)) lines.push(`--accent-text: ${t.accent_text};`);
  if (HEX.test(t.bg)) lines.push(`--bg: ${t.bg};`);
  return lines.length ? `${selector} { ${lines.join(' ')} }` : '';
}

/** The CSS a branding produces: token overrides for both themes (empty for the Castfeeds brand). */
export function brandingCss(b: PublicBranding | null): string {
  if (!isTenantBrand(b)) return '';
  return [block(":root, [data-theme='dark']", b.theme.dark), block("[data-theme='light']", b.theme.light)].filter(Boolean).join('\n');
}

/** Apply a branding to the document: token overrides, favicon, title. Passing null restores Castfeeds. */
export function applyBranding(b: PublicBranding | null, doc: Document = document): void {
  let style = doc.getElementById(STYLE_ID) as HTMLStyleElement | null;
  const css = brandingCss(b);
  if (css) {
    if (!style) {
      style = doc.createElement('style');
      style.id = STYLE_ID;
      doc.head.appendChild(style);
    }
    style.textContent = css;
  } else style?.remove();

  const name = isTenantBrand(b) ? b.product_name : 'Castfeeds';
  doc.title = name;
  const icon = doc.querySelector<HTMLLinkElement>('link[rel="icon"]');
  const fav = isTenantBrand(b) ? safeAssetUrl(b.favicon_url) : null;
  if (icon) {
    icon.href = fav ?? '/favicon.svg';
    icon.type = fav?.endsWith('.png') ? 'image/png' : fav?.endsWith('.ico') ? 'image/x-icon' : 'image/svg+xml';
  }
  doc.documentElement.dataset.brand = isTenantBrand(b) ? 'tenant' : 'castfeeds';
}

/**
 * Fetch and apply the host's branding. Never throws: on any failure the app keeps the Castfeeds brand.
 * A tenant's default language applies only to visitors who never picked one.
 */
export async function loadBranding(timeoutMs = 2500): Promise<PublicBranding | null> {
  const timer = new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs));
  try {
    const b = await Promise.race([api.branding.forHost(), timer]);
    if (!b) {
      logWarn('branding', `no answer within ${timeoutMs} ms; using the Castfeeds brand`);
      useBranding.getState().set(null, 'error');
      return null;
    }
    applyBranding(b);
    useBranding.getState().set(b, 'ready');
    let chosen = false;
    try {
      chosen = localStorage.getItem(LOCALE_CHOSEN_KEY) === '1';
    } catch {
      chosen = false;
    }
    if (isTenantBrand(b) && b.default_locale && !chosen) usePrefs.getState().setLocale(b.default_locale);
    logInfo('branding', `brand=${b.source} host=${b.host ?? '-'} purpose=${b.purpose ?? '-'} status=${b.domain_status ?? '-'}`);
    return b;
  } catch (e) {
    logWarn('branding', 'could not load the branding; using the Castfeeds brand', e);
    useBranding.getState().set(null, 'error');
    return null;
  }
}
